import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import SetupChecklist from '../components/SetupChecklist';

const formatDate = (d) => {
  if (!d) return '';
  const date = new Date(d);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':');
  const date = new Date();
  date.setHours(parseInt(h || '0', 10), parseInt(m || '0', 10), 0, 0);
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const diff = new Date(dateStr) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

const DueBadge = ({ days }) => {
  if (days === null) return null;
  if (days < 0)  return <span className="badge badge-red">Overdue</span>;
  if (days === 0) return <span className="badge badge-red">Today</span>;
  if (days <= 7)  return <span className="badge badge-red">{days}d</span>;
  if (days <= 21) return <span className="badge badge-amber">{days}d</span>;
  return <span className="badge badge-green">{days}d</span>;
};


const formatWeatherHorseSummary = (horseNames = []) => {
  const names = Array.isArray(horseNames) ? horseNames.filter(Boolean) : [];
  if (names.length === 0) return 'Applies to this weather rule';
  if (names.length === 1) return `Applies to ${names[0]}`;
  if (names.length === 2) return `Applies to ${names[0]} and ${names[1]}`;
  if (names.length === 3) return `Applies to ${names[0]}, ${names[1]}, and ${names[2]}`;
  return `Applies to ${names[0]}, ${names[1]}, ${names[2]} +${names.length - 3} more`;
};

const weatherTitle = (n) => n?.title && n.title !== '*WEATHER ALERT*' ? n.title : 'Weather Watch';

const eventTypeColor = {
  lesson: 'var(--cr-green)', show: 'var(--cr-pink)', farrier: 'var(--cr-purple)',
  vet: 'var(--cr-blue)', bodywork: 'var(--cr-green-dark)', worming: 'var(--cr-amber)',
  vaccine: 'var(--cr-amber)', other: 'var(--text-muted)',
};

const eventTypeBadge = {
  lesson: 'badge-green', show: 'badge-pink', farrier: 'badge-purple',
  vet: 'badge-blue', bodywork: 'badge-green', worming: 'badge-amber',
  vaccine: 'badge-amber', other: 'badge-gray',
};

const WelcomeDashboard = ({ setupState, signingNotices = [] }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.primary_role || 'owner';
  return (
    <div>
      <div className="top-bar">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h1 style={{ fontSize: 18 }}>Welcome, {user?.first_name}</h1>
            <p style={{ opacity: 0.85, fontSize: 12, marginTop: 2 }}>Horse care, community & marketplace</p>
          </div>
          <Link to="/profile" style={{ textDecoration: 'none' }}>
            <div className="avatar avatar-md" style={{ background: 'rgba(255,255,255,0.2)' }}>
              <span style={{ color: 'white', fontSize: 16 }}>{user?.first_name?.[0]}{user?.last_name?.[0]}</span>
            </div>
          </Link>
        </div>
      </div>

      <div className="page-content">
        {signingNotices.length > 0 && (
          <>
            <div className="section-label">Action needed</div>
            {signingNotices.map(n => (
              <div key={n.id} className="card" onClick={() => navigate(n.link)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', cursor: 'pointer', border: '1px solid var(--cr-primary)', background: 'var(--cr-primary-light)' }}>
                <span style={{ fontSize: 20 }}>✍️</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--cr-primary)' }}>{n.title}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>{n.body}</div>
                </div>
                <span style={{ color: 'var(--cr-primary)', fontSize: 18 }}>›</span>
              </div>
            ))}
          </>
        )}
        {setupState && <SetupChecklist state={setupState} />}
        <div className="card">
          <h2 style={{ fontSize: 15, marginBottom: 6 }}>Everything your horse needs, in one place</h2>
          <p style={{ fontSize: 13, marginBottom: 14 }}>Manage your horses, lessons, supplements, and reorder supplies — all from your phone.</p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 14 }}>
            {[
              'Track vaccines, worming & vet visits',
              'Schedule lessons, farrier & bodywork',
              'Reorder supplies from The Connected Rider',
              'Connect with your barn & trainer',
              'Buy & sell used tack in the community',
              'Upcoming shows, prep checklists & shopping',
            ].map(f => (
              <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-secondary)' }}>
                <span style={{ color: 'var(--cr-green)', fontWeight: 600, fontSize: 14 }}>✓</span> {f}
              </div>
            ))}
          </div>

          <div style={{ height: '0.5px', background: 'var(--border-color)', margin: '12px 0' }} />
          <p style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 10 }}>To get started, add your horses</p>

          <button onClick={() => navigate('/horses/add')}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, width: '100%', padding: '16px', background: 'var(--cr-primary)', color: 'white', fontSize: 15, fontWeight: 600, border: 'none', borderRadius: 12, cursor: 'pointer' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            ADD YOUR HORSES
          </button>
        </div>

        <div className="section-label">Explore while you get set up</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {[
            { icon: '👥', label: 'Community', sub: 'Follow horses & riders', path: '/community' },
            { icon: '🛍️', label: 'Shop', sub: 'Tack & supplies', path: '/shop' },
            { icon: '🏷️', label: 'Marketplace', sub: 'Buy & sell used tack', path: '/marketplace' },
            { icon: '📅', label: 'Calendar', sub: 'Lessons & events', path: '/calendar' },
            { icon: '🏆', label: 'Show Center', sub: 'Credentials, show packets, secretaries', path: '/shows' },
          ].map(item => (
            <button key={item.label} onClick={() => navigate(item.path)}
              style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-lg)', padding: '12px 10px', cursor: 'pointer', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 22 }}>{item.icon}</span>
              <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{item.label}</span>
              <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{item.sub}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const HomePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Barn staff own no horses, so this dashboard is empty for them.
  const isBarnStaff = user?.account_type === 'barn_staff';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupState, setSetupState] = useState(null);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  useEffect(() => {
    api.get('/horses/dashboard').then(r => {
      setData(r.data);
    }).catch(() => toast.error('Failed to load dashboard')).finally(() => setLoading(false));
    api.get('/auth/setup/state').then(r => setSetupState(r.data)).catch(() => {});
  }, []);


  const [reorderFeedback, setReorderFeedback] = useState(null);
  const [trivia, setTrivia] = useState(null);
  const [triviaPick, setTriviaPick] = useState(null);
  const [showPunch, setShowPunch] = useState(false);
  const [showTriviaInfo, setShowTriviaInfo] = useState(false);
  const [triviaHidden, setTriviaHidden] = useState(false);
  const [funWeather, setFunWeather] = useState(() => {
    try { return localStorage.getItem('cr_fun_weather') !== 'off'; } catch { return true; }
  });
  const toggleFunWeather = () => {
    setFunWeather(v => {
      try { localStorage.setItem('cr_fun_weather', v ? 'off' : 'on'); } catch {}
      return !v;
    });
  };

  const FUN_WEATHER = {
    heat: [
      "It's hotter than a black saddle in July. Water checks, people.",
      "Even the flies are looking for shade today.",
      "Sweat scrapers on standby. It's a scorcher.",
      "Today's forecast: fly spray evaporating on contact.",
      "The arena footing is officially lava.",
      "Hydrate like a lesson kid at summer camp.",
      "The horses voted: turnout at dawn or not at all.",
      "Shade is the most valuable real estate in the pasture right now.",
      "The humidity is 90%. Your horse is 90% sweat. Math checks out.",
      "It's not just hot — the air itself needs a sweat scraper.",
      "Today's air: chewable. Serve with electrolytes.",
      "You'll be soaked before the first stall is done. It's not rain. It's Texas.",
      "The fans are on max and the horses still stand like they pay the electric bill.",
      "Fly masks on, fans blasting, and everyone's still mad about it.",
      "Hose-down season: the horses tolerate it, the barn dogs demand it.",
      "Two shirts minimum today — one for chores, one for pretending you didn't do chores.",
      "Electrolytes for the horses. Electrolytes for you. Electrolytes for the barn cat, probably.",
      "The water trough is now a hot tub. Refill accordingly.",
    ],
    cold: [
      "Your horses have built-in blankets. You don't. Layer up.",
      "Water trough ice check — nobody likes crunchy hydration.",
      "It's colder than a farrier's answer to 'can you come today?'",
      "Frost on the whiskers, sass in the trot.",
      "Today's workout: breaking trough ice and regretting life choices.",
      "The horses are fluffy. The tack is frozen. Balance.",
      "Blanket math hour: is 40 and sunny a sheet, a midweight, or a debate?",
      "Fresh snow: nature's way of making every horse forget they're broke.",
      "Bit warmer weather. Nobody's putting frozen metal in that mouth and you know it.",
      "Double boot socks tonight — style points don't count in the barn aisle.",
      "Heated water buckets: the greatest invention since the hay net. Fight me.",
      "Hot chocolate in the tack room counts as barn chores if you're supervising.",
      "Toe warmers in the boots, hand warmers in the gloves, dignity in the truck.",
      "Layer up till you can't put your arms down. Barn snowman is a valid look.",
      "The muck fork handle is a lie. It's a popsicle now.",
      "Barn cats have claimed every heated surface. Negotiate at your own risk.",
      "Your breath, the horses' breath, the coffee steam — everything's smoking but the heater.",
      "Frozen gate chains: today's finger workout.",
      "80 yesterday, 35 tomorrow. Texas said 'choose a blanket, any blanket, you're wrong.'",
      "Blanket on, blanket off, blanket on — congratulations, that's the Texas workout.",
    ],
    rain: [
      "Free bath day! The mud is complimentary.",
      "Somewhere, a freshly bathed gray horse is planning to roll.",
      "The pasture is now a water feature.",
      "White horses everywhere are about to become brown horses.",
      "Today's color palette: mud, mud, and dappled mud.",
      "The horses will stand in the rain ignoring their perfectly good shelter. As is tradition.",
      "Rain rot patrol starts now — check those backs.",
      "Good news: the pastures are getting watered. Bad news: everything else.",
      "Muck boots: don't fight the mud without them. It's undefeated.",
      "The mud will take a boot today. Lace them tight and say a prayer.",
      "Rain sheet or naked and filthy — every horse has chosen. Chosen wrong.",
      "That squelch sound is the pasture keeping your boot as a souvenir.",
      "Towels by the door, mud in the aisle, glory in surviving it.",
    ],
    storm: [
      "The sky's throwing a tantrum. Maybe don't be the tallest thing in the pasture.",
      "Thunder: nature's way of testing your fencing and your nerves.",
      "Storm rolling in — the barn cats called it an hour ago.",
      "The radar looks like abstract art. The dramatic kind.",
      "Perfect evening to double-check gate latches and count the flashlights.",
      "The horses knew before your weather app did.",
      "Storm's coming — somewhere a tarp is preparing its escape.",
      "Tonight's entertainment: watching the herd pretend they're not spooked.",
      "Charge your phone, find the lanterns, and accept that the horses are calmer than you.",
      "Tonight's forecast: thunder, followed by a herd standing in the open ignoring it.",
      "Secure anything that flaps. The horses have declared war on anything that flaps.",
    ],
    wind: [
      "Everything is a horse-eating monster in this wind. Good luck.",
      "Today's forecast: airborne feed buckets and dramatic tails.",
      "The wind has opinions. So will your horses.",
      "Plot twist: every horse is now 17 hands and airborne.",
      "That plastic bag three pastures over? Your horse has already seen it.",
      "Manes: 100% dramatic. Flags: unnecessary. The horses ARE the flags.",
      "Wind advisory = free lunging, whether you planned it or not.",
      "Hold onto your hat. And your pony.",
      "Today's wind: strong enough to re-park the muck cart on its own.",
      "Hay delivery in this wind? Half for the horses, half for the county.",
      "Arena drag day is canceled — the wind already rearranged it.",
    ],
    other: [
      "The weather has opinions about your plans today.",
      "Mother Nature is keeping things interesting.",
      "The weather app and the horses disagree. Trust the horses.",
      "Whatever the sky's doing, the hay still needs throwing.",
      "The forecast changed four times since you fed. It'll change twice more before night check.",
    ],
  };

  const funWeatherLine = (n) => {
    const label = (weatherTitle(n) || '').toLowerCase() + ' ' + (n.action_text || '').toLowerCase();
    let key = 'other';
    if (/heat|hot|temperature high|scorch/.test(label)) key = 'heat';
    else if (/cold|freez|frost|chill|winter/.test(label)) key = 'cold';
    else if (/storm|thunder|lightning|severe/.test(label)) key = 'storm';
    else if (/rain|shower|wet|precip/.test(label)) key = 'rain';
    else if (/wind|gust/.test(label)) key = 'wind';
    const lines = FUN_WEATHER[key];
    const day = Math.floor(Date.now() / 86400000);
    const seed = (n.id || '').split('').reduce((a, c) => a + c.charCodeAt(0), day);
    return lines[seed % lines.length];
  };

  const hideTriviaToday = () => {
    try { localStorage.setItem('cr_trivia_hidden', trivia?.day || ''); } catch {}
    setTriviaHidden(true);
  };

  useEffect(() => {
    api.get('/trivia/today').then(r => {
      setTrivia(r.data);
      try { if (localStorage.getItem('cr_trivia_hidden') === r.data.day) setTriviaHidden(true); } catch {}
    }).catch(() => {});
  }, []);

  const answerTrivia = async (i) => {
    if (!trivia || trivia.answered || triviaPick !== null) return;
    setTriviaPick(i);
    try {
      const { data: res } = await api.post('/trivia/answer', { choice_index: i });
      setTrivia(prev => ({ ...prev, answered: true, your_choice: i,
        correct_index: res.correct_index, was_correct: res.correct,
        points: res.points, level: res.level }));
      if (res.correct) toast.success('+1! ' + res.points + ' total — ' + res.level.emoji + ' ' + res.level.name);
    } catch { setTriviaPick(null); }
  };

  const handleCompleteReminder = async (r) => {
    // Supplement/supply reorder reminders trigger the learning feedback prompt
    if ((r.source === 'supplement' || r.source === 'supply') && r.source_id) {
      setReorderFeedback(r);
      return;
    }
    try {
      await api.put(`/reminders/${r.id}/complete`);
      setData(prev => ({ ...prev, pinnedReminders: prev.pinnedReminders.filter(x => x.id !== r.id) }));
      toast.success('Done!');
    } catch { toast.error('Failed'); }
  };


  const handleWeatherAction = async (eventId, action) => {
    const endpoints = {
      dismiss: `/weather-watch/events/${eventId}/dismiss`,
      snooze: `/weather-watch/events/${eventId}/snooze`,
      handled: `/weather-watch/events/${eventId}/handled`,
    };
    try {
      if (action === 'snooze') await api.put(endpoints.snooze, { minutes: 120 });
      else await api.put(endpoints[action]);
      setData(prev => ({
        ...prev,
        weatherNotices: (prev.weatherNotices || []).filter(n => n.id !== eventId),
      }));
      toast.success(action === 'snooze' ? 'Reminder snoozed' : action === 'handled' ? 'Marked handled' : 'Dismissed for today');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Weather action failed');
    }
  };

  const submitReorderFeedback = async (feedback) => {
    const r = reorderFeedback;
    const endpoint = r.source === 'supply'
      ? `/supplies/supply/${r.source_id}/reorder-feedback`
      : `/catalog/supplement/${r.source_id}/reorder-feedback`;
    try {
      const { data: res } = await api.post(endpoint, { feedback });
      setData(prev => ({ ...prev, pinnedReminders: prev.pinnedReminders.filter(x => x.id !== r.id) }));
      toast.success(res.message || 'Logged');
    } catch { toast.error('Failed'); } finally { setReorderFeedback(null); }
  };

  const handleAcknowledgeConcern = async (id) => {
    try {
      await api.post(`/horse-checks/${id}/acknowledge`);
      setData(prev => ({ ...prev, horseConcerns: (prev?.horseConcerns || []).filter(c => c.id !== id) }));
      toast.success('Concern acknowledged');
    } catch { toast.error('Failed to acknowledge concern'); }
  };

  const addReminderToShopList = async (r) => {
    const name = (r.title || '').replace(/^reorder\s*:?-?\s*/i, '').trim() || r.title;
    try {
      await api.post('/shopping/list/items', { product_name: name, source: 'manual', quantity: 1 });
      toast.success(`${name} added to your Shop list`);
    } catch { toast.error('Failed to add to list'); }
  };

  if (loading) return (
    <div>


      <div className="top-bar"><h1 style={{ color: 'white' }}>Connected Rider</h1></div>
      <div className="loading-screen"><div className="spinner" /><p>Loading...</p></div>
    </div>
  );

  const horses = data?.horses || [];

  // No horses yet — show welcome dashboard with setup checklist
  if (horses.length === 0) return <WelcomeDashboard setupState={setupState} signingNotices={data?.signingNotices || []} />;

  const vaccinesDue = data?.vaccinesDue || [];
  const wormersDue = data?.wormersDue || [];
  const upcomingEvents = data?.upcomingEvents || [];
  const pinnedReminders = data?.pinnedReminders || [];
  const feedingNotices = data?.feedingNotices || [];
  const weatherNotices = data?.weatherNotices || [];
  const horseConcerns = data?.horseConcerns || [];
  const allDue = [
    ...vaccinesDue.map(v => ({ ...v, kind: 'vaccine', label: v.name, due: v.next_due_date })),
    ...wormersDue.map(w => ({ ...w, kind: 'wormer', label: w.product_name, due: w.next_due_date })),
  ].sort((a, b) => new Date(a.due) - new Date(b.due));

  return (
    <div>
      <div className="top-bar">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h1 style={{ fontSize: 18 }}>{greeting}, {user?.first_name}</h1>
            <p style={{ opacity: 0.85, fontSize: 12, marginTop: 2 }}>{today}</p>
          </div>
          <Link to="/profile" style={{ textDecoration: 'none' }}>
            <div className="avatar avatar-md" style={{ background: 'rgba(255,255,255,0.2)' }}>
              {user?.avatar_url
                ? <img src={user.avatar_url} alt={user.first_name} />
                : <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="22" height="22"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>}
            </div>
          </Link>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 12 }}>
          {[
            { num: horses.length, label: `Horse${horses.length !== 1 ? 's' : ''}` },
            { num: upcomingEvents.length, label: 'Upcoming' },
            { num: allDue.length, label: 'Due soon', alert: allDue.length > 0 },
          ].map(s => (
            <div key={s.label} style={{ background: '#F1EAE0', borderRadius: '16px', padding: '8px 10px', textAlign: 'center', color: '#4B002B' }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: s.alert ? '#B4552E' : '#4B002B' }}>{s.num}</div>
              <div style={{ fontSize: 10, color: '#7A5A45', marginTop: 1 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="page-content">
        {setupState && <SetupChecklist state={setupState} />}
        {horseConcerns.length > 0 && (
          <>
            <div className="section-label">Horse concerns</div>
            {horseConcerns.map(c => (
              <div key={c.id} className="card" style={{ border: '1px solid #E39A52', background: '#FFF7EC', padding: '12px 14px', marginBottom: 10 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 20, marginTop: 1 }}>⚠️</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 10, fontWeight: 800, color: '#C76822', textTransform: 'uppercase', letterSpacing: .4 }}>Horse concern</div>
                    <div style={{ fontSize: 14, fontWeight: 700, marginTop: 2 }}>{c.horse_name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>{c.summary}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>Reported by {c.recorder_name} · {new Date(c.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <button className="btn btn-primary btn-sm" onClick={() => navigate('/horses/daily-check')}>Review</button>
                    <button onClick={() => handleAcknowledgeConcern(c.id)} style={{ border: 'none', background: 'none', color: 'var(--cr-primary)', fontSize: 10, cursor: 'pointer' }}>Acknowledge</button>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}

        {/* Weather Watch notices */}
        {weatherNotices.length > 0 && (
          <>
            <div className="section-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>Weather Watch</span>
              <span style={{ flex: 1 }} />
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontWeight: 400, textTransform: 'none', fontSize: 11, color: 'var(--text-muted)' }}>
                <input type="checkbox" checked={funWeather} onChange={toggleFunWeather} style={{ accentColor: 'var(--cr-primary)' }} />
                😄 Fun mode
              </label>
            </div>
            {weatherNotices.map(n => (
              <div key={n.id} className="card" onClick={() => navigate('/weather-watch')}
                style={{ cursor: 'pointer', border: '1px solid var(--cr-amber)', background: 'rgba(255, 193, 7, 0.12)', padding: '12px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <span style={{ fontSize: 20, marginTop: 2 }}>⚠️</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--cr-amber)', textTransform: 'uppercase', letterSpacing: 0.3 }}>{weatherTitle(n)}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginTop: 2 }}>{n.action_text}</div>
                    {n.message && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{n.message}</div>}
                    {funWeather && <div style={{ fontSize: 12, fontStyle: 'italic', color: 'var(--cr-primary)', marginTop: 4 }}>{funWeatherLine(n)}</div>}
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{formatWeatherHorseSummary(n.horse_names)}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }} onClick={e => e.stopPropagation()}>
                      <button className="btn btn-secondary btn-sm" onClick={() => handleWeatherAction(n.id, 'handled')}>Mark handled</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => handleWeatherAction(n.id, 'snooze')}>Remind later</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => handleWeatherAction(n.id, 'dismiss')}>Dismiss today</button>
                    </div>
                  </div>
                  <span style={{ color: 'var(--text-muted)', fontSize: 18, marginTop: 2 }}>›</span>
                </div>
              </div>
            ))}
          </>
        )}

        {/* Document signing notices — non-dismissible until signed */}
        {(data?.signingNotices || []).length > 0 && (
          <>
            <div className="section-label">Action needed</div>
            {data.signingNotices.map(n => (
              <div key={n.id} className="card" onClick={() => navigate(n.link)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', cursor: 'pointer', border: '1px solid var(--cr-primary)', background: 'var(--cr-primary-light)' }}>
                <span style={{ fontSize: 20 }}>✍️</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--cr-primary)' }}>{n.title}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>{n.body}</div>
                </div>
                <span style={{ color: 'var(--cr-primary)', fontSize: 18 }}>›</span>
              </div>
            ))}
          </>
        )}

        {/* Consolidated feeding notices */}
        {feedingNotices.length > 0 && (
          <>
            <div className="section-label">Feed & supplement notices</div>
            {feedingNotices.map(n => (
              <div key={n.time} className="card" onClick={() => navigate('/horses')}
                style={{ cursor: 'pointer', borderTop: '1px solid rgba(75,0,43,0.28)', borderRight: '1px solid rgba(75,0,43,0.28)', borderBottom: '1px solid rgba(75,0,43,0.28)', borderLeft: '4px solid #4B002B', borderRadius: '0 14px 14px 0', background: '#F8F0EC', padding: '12px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <span style={{ fontSize: 20 }}>🥣</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--cr-primary)' }}>{formatTime(n.time)} care</div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{n.horses.length} horse{n.horses.length !== 1 ? 's' : ''} due at this time</div>
                  </div>
                  <span style={{ color: 'var(--cr-primary)', fontSize: 18 }}>›</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {(() => {
                    const s = n.summary;
                    if (!s) return null;
                    const row = (label, text) => text ? (
                      <div key={label} style={{ fontSize: 12, color: 'var(--text-primary)' }}>
                        <strong style={{ color: 'var(--cr-primary)' }}>{label}</strong> &middot; {text}
                      </div>
                    ) : null;
                    const stacksOf = k => (s.stacks || []).filter(x => x.kind === k);
                    const describe = (kind, fallbackCount, fallbackNoun) => {
                      const st = stacksOf(kind);
                      if (st.length) return st.map(x => x.name + ' (' + x.item_count + ')').join(', ');
                      if (fallbackCount > 0) return fallbackCount + ' ' + fallbackNoun + (fallbackCount !== 1 ? 's' : '');
                      return null;
                    };
                    const feedText = stacksOf('feed').length
                      ? describe('feed')
                      : ((s.feed_horses || []).length ? s.feed_horses.join(', ') : null);
                    const hayText = stacksOf('hay').length
                      ? describe('hay')
                      : ((s.hay_horses || []).length ? s.hay_horses.join(', ') : null);
                    const suppText = describe('supplement', (s.counts || {}).supplement, 'supplement');
                    return [
                      row('Feed', feedText),
                      row('Supplements', suppText),
                      row('Hay', hayText)
                    ].filter(Boolean);
                  })()}
                </div>
              </div>
            ))}
          </>
        )}

        {/* Pinned reminders */}
        {pinnedReminders.length > 0 && (
          <>
            <div className="section-label">Reminders</div>
            {(() => {
              const groups = [];
              const byKey = {};
              const dueDay = value => value ? String(value).slice(0, 10) : '';
              const compactNames = names => names.length <= 2 ? names.join(', ') : `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
              const horseForReminder = r => horses.find(h => h.id === r.horse_id);
              const horseNameFor = r => horseForReminder(r)?.name || String(r.title || '').replace(/^Farrier\s+[—-]\s*/, '') || 'Horse';

              pinnedReminders.forEach(r => {
                const coggins = String(r.title || '').match(/^(.*?)'s (Coggins.*)$/);
                let key = null, label = null, horse = null, kind = null, provider = '';

                if (r.source === 'farrier') {
                  provider = horseForReminder(r)?.farrier_name || '';
                  key = `farrier|${dueDay(r.due_date)}|${provider.toLowerCase()}`;
                  label = 'Farrier care due';
                  horse = horseNameFor(r);
                  kind = 'farrier';
                } else if (coggins && r.title.includes("'s ")) {
                  key = `${coggins[2]}|${dueDay(r.due_date)}`;
                  label = coggins[2];
                  horse = coggins[1];
                  kind = 'grouped';
                } else if (/^Time to book the farrier\b/.test(r.title)) {
                  key = `farrier|${dueDay(r.due_date)}`;
                  label = 'Farrier care due';
                  horse = r.title.replace(/^Time to book the farrier[^A-Za-z0-9]*/, '') || 'Horse';
                  kind = 'farrier';
                }

                if (!key) {
                  groups.push({ id: r.id, single: r });
                  return;
                }

                if (!byKey[key]) {
                  byKey[key] = { id: 'g-' + key, label, kind, due_date: r.due_date, provider, horses: [], horseIds: [], items: [] };
                  groups.push(byKey[key]);
                }
                if (horse && !byKey[key].horses.includes(horse)) byKey[key].horses.push(horse);
                if (r.horse_id && !byKey[key].horseIds.includes(r.horse_id)) byKey[key].horseIds.push(r.horse_id);
                byKey[key].items.push(r);
              });

              return groups.map(g => {
                if (g.single) {
                  const r = g.single;
                  return (
                    <div key={r.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px' }}>
                      <button onClick={() => handleCompleteReminder(r)}
                        style={{ width: 22, height: 22, borderRadius: '50%', border: '1.5px solid var(--border-strong)', background: 'none', cursor: 'pointer', flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{r.title}</div>
                        {r.due_date && <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>{formatDate(r.due_date)}</div>}
                      </div>
                      {(r.source === 'supplement' || r.source === 'supply') && (
                        <button onClick={() => addReminderToShopList(r)} title="Add to Shop list"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: '0 2px', flexShrink: 0 }}>🛒</button>
                      )}
                    </div>
                  );
                }

                if (g.kind === 'farrier') {
                  return (
                    <div key={g.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>Farrier care due</div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                          {g.due_date ? formatDate(g.due_date) : 'Due'} · {g.horses.length} horse{g.horses.length === 1 ? '' : 's'}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{compactNames(g.horses)}</div>
                        {g.provider && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{g.provider}</div>}
                      </div>
                      <button className="btn btn-primary btn-sm" onClick={() => navigate('/appointments', { state: { appointmentPrefill: { type: 'farrier', horse_ids: g.horseIds, proposed_date: dueDay(g.due_date) } } })}>
                        Book ›
                      </button>
                    </div>
                  );
                }

                return (
                  <div key={g.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px' }}>
                    <button onClick={async () => { for (const r of g.items) await handleCompleteReminder(r); }}
                      style={{ width: 22, height: 22, borderRadius: '50%', border: '1.5px solid var(--border-strong)', background: 'none', cursor: 'pointer', flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{g.label} — {g.horses.length} horses</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{compactNames(g.horses)}</div>
                      {g.due_date && <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>{formatDate(g.due_date)}</div>}
                    </div>
                  </div>
                );
              });
            })()}
            {false && pinnedReminders.map(r => (
              <div key={r.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px' }}>
                <button onClick={() => handleCompleteReminder(r)}
                  style={{ width: 22, height: 22, borderRadius: '50%', border: '1.5px solid var(--border-strong)', background: 'none', cursor: 'pointer', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{r.title}</div>
                  {r.due_date && <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>Due {formatDate(r.due_date)}</div>}
                </div>
                {(r.source === 'supplement' || r.source === 'supply') && (
                  <button onClick={() => addReminderToShopList(r)} title="Add to Shop list"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: '0 2px', flexShrink: 0 }}>🛒</button>
                )}
              </div>
            ))}
          </>
        )}

        {trivia && trivia.question && !triviaHidden && (
          <>
            <div className="section-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>{trivia.kind === 'joke' ? '😄 Daily Chuckle' : '🐴 Daily Trivia'}</span>
              <button onClick={() => setShowTriviaInfo(v => !v)} title="How this works"
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text-muted)', padding: 0 }}>ⓘ</button>
              <span style={{ flex: 1 }} />
              <button onClick={hideTriviaToday} title="Hide for today"
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--text-muted)', padding: 0, lineHeight: 1 }}>×</button>
            </div>
            {showTriviaInfo && (
              <div className="card" style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
                A new question every day — jokes on Sundays and Wednesdays. Each correct answer earns 1 point,
                and points move you up the show ladder: 🎀 White → Yellow → Red → Blue Ribbon, then
                🏆 Bronze → Silver → Gold → Platinum Trophy. Your ribbon shows next to your name in Community.
                One try per day, so make it count!
              </div>
            )}
            <div className="card" style={{ padding: '12px 14px' }}>
              <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 10 }}>{trivia.question}</div>

              {trivia.kind === 'joke' && (
                !showPunch
                  ? <button className="btn btn-secondary btn-full" onClick={() => setShowPunch(true)}>Show me</button>
                  : <div style={{ fontSize: 14, color: 'var(--cr-primary)', fontWeight: 600 }}>{trivia.punchline}</div>
              )}

              {trivia.kind === 'trivia' && (trivia.choices || []).map((c, i) => {
                const done = trivia.answered;
                const isCorrect = done && i === trivia.correct_index;
                const isYourMiss = done && i === trivia.your_choice && !isCorrect;
                return (
                  <button key={i} onClick={() => answerTrivia(i)} disabled={done || triviaPick !== null}
                    className="btn btn-full"
                    style={{ marginBottom: 6, textAlign: 'left', justifyContent: 'flex-start',
                      border: '1px solid var(--border-color)',
                      background: isCorrect ? 'var(--cr-green)' : isYourMiss ? 'var(--danger-color, #c0392b)' : 'var(--bg-secondary)',
                      color: (isCorrect || isYourMiss) ? 'white' : 'var(--text-primary)', fontSize: 13 }}>
                    {String.fromCharCode(65 + i)}. {c}{isCorrect ? ' ✓' : ''}
                  </button>
                );
              })}

              {trivia.answered && (
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>
                  {trivia.was_correct ? 'Nailed it! ' : 'Good try — come back tomorrow. '}
                  {trivia.points} point{trivia.points === 1 ? '' : 's'} · {trivia.level.emoji} {trivia.level.name}
                  {trivia.level.next && <> · {trivia.level.next.to_go} to {trivia.level.next.name}</>}
                </div>
              )}
            </div>
          </>
        )}

        {/* Health alerts */}
        {allDue.length > 0 && (
          <>
            <div className="section-label">Health alerts</div>
            <div className="card" style={{ padding: '8px 14px' }}>
              {allDue.slice(0, 5).map((item, i) => {
                const days = daysUntil(item.due);
                return (
                  <div key={i} className="list-row" style={{ cursor: 'pointer' }} onClick={() => navigate('/horses')}>
                    <span style={{ fontSize: 18 }}>{item.kind === 'vaccine' ? '💉' : '💊'}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{item.label}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1 }}>Due {formatDate(item.due)}</div>
                    </div>
                    <DueBadge days={days} />
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Upcoming events */}
        {upcomingEvents.length > 0 && (
          <>
            <div className="section-label">Upcoming</div>
            <div className="card" style={{ padding: '8px 14px' }}>
              {upcomingEvents.map(event => {
                const dt = new Date(event.start_datetime);
                const isToday = dt.toDateString() === new Date().toDateString();
                const isTomorrow = dt.toDateString() === new Date(Date.now() + 86400000).toDateString();
                const dateLabel = isToday ? 'Today' : isTomorrow ? 'Tomorrow' : formatDate(event.start_datetime);
                const timeLabel = event.all_day ? 'All day' : dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                return (
                  <div key={event.id} className="list-row" style={{ cursor: 'pointer' }} onClick={() => navigate('/calendar')}>
                    <div style={{ width: 4, alignSelf: 'stretch', borderRadius: 2, background: eventTypeColor[event.event_type] || 'var(--cr-green)', flexShrink: 0, marginRight: 4 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{event.title}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1 }}>{dateLabel} · {timeLabel}</div>
                      {event.horse_names?.length > 0 && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                          🐴 {event.horse_names.join(', ')}
                        </div>
                      )}
                    </div>
                    <span className={`badge ${eventTypeBadge[event.event_type] || 'badge-gray'}`} style={{ fontSize: 10, textTransform: 'capitalize' }}>{event.event_type}</span>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Quick actions */}
        <div className="section-label">Quick actions</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {[
            { label: 'Add reminder', icon: '🔔', path: '/reminders' },
            { label: 'Log health', icon: '💉', path: horses[0] ? `/horses/${horses[0].id}/health` : '/horses' },
            { label: 'Add event', icon: '📅', path: '/calendar/add' },
            { label: 'Shop', icon: '🛒', path: '/shop' },
          ].map(action => (
            <button key={action.label} onClick={() => navigate(action.path)}
              style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-lg)', padding: '14px 12px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 22 }}>{action.icon}</span>
              <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{action.label}</span>
            </button>
          ))}
        </div>
      </div>


      {reorderFeedback && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'flex-end' }}>
          <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: 20, width: '100%', maxWidth: 480, margin: '0 auto' }}>
            <h2 style={{ fontSize: 17, marginBottom: 4 }}>Reorder logged</h2>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>Was this reminder timed right? Your answer helps us predict the next one.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button className="btn btn-primary btn-full" onClick={() => submitReorderFeedback('on_time')}>👍 Perfect timing</button>
              <button className="btn btn-secondary btn-full" onClick={() => submitReorderFeedback('too_early')}>⏪ Too early — I still had plenty</button>
              <button className="btn btn-secondary btn-full" onClick={() => submitReorderFeedback('too_late')}>⏩ Too late — I'd already run out</button>
            </div>
            <button className="btn btn-secondary btn-full" style={{ marginTop: 8 }} onClick={() => addReminderToShopList(reorderFeedback)}>🛒 Add to my Shop list</button>
            <button onClick={() => setReorderFeedback(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 13, marginTop: 12, width: '100%' }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
